// Editors' read-only monitoring: answers submitted overall, each Sentinel's progress and time per case over the last
// frame days, then per brand and frame day the cases and each Sentinel's submitted and in-progress answers.

import { useAtomValue } from 'jotai';
import { format } from 'date-fns';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { GoldenAdapter } from '@/adapters/golden.adapter';
import { getApiErrorMessage, parseUtcDate } from '@/lib/utils';
import type { GoldenProgress } from '@/types/golden.types';
import { benchmarkProgressQueryAtom } from './benchmark-state';

const NONE = '—';

function SentinelTable({ progress }: { progress: GoldenProgress }) {
  if (progress.sentinels.length === 0) {
    return <p className="py-6 text-center text-[13px] text-gray-800">No Sentinels have cases in the last 7 frame days.</p>;
  }
  // Rows come sorted from the API: lowest % done first.
  return (
    <Table aria-label="Sentinel progress">
      <TableHeader>
        <TableRow>
          <TableHead>Sentinel</TableHead>
          <TableHead>Brand(s)</TableHead>
          <TableHead>Submitted</TableHead>
          <TableHead>In progress</TableHead>
          <TableHead>Not started</TableHead>
          <TableHead>% done</TableHead>
          <TableHead>Median min/case</TableHead>
          <TableHead>Rushed</TableHead>
          <TableHead>Last answer</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {progress.sentinels.map((sentinel) => (
          <TableRow key={sentinel.email}>
            <TableCell>{sentinel.email}</TableCell>
            <TableCell>{sentinel.brands.map(GoldenAdapter.brandName).join(', ')}</TableCell>
            <TableCell className="tabular-nums">{sentinel.submitted}</TableCell>
            <TableCell className="tabular-nums">{sentinel.inProgress}</TableCell>
            <TableCell className="tabular-nums">{sentinel.notStarted}</TableCell>
            <TableCell>
              <span className="flex items-center gap-2">
                <Progress value={sentinel.percentDone} aria-label={`${sentinel.email}: ${sentinel.percentDone}% done`} className="w-16" />
                <span className="tabular-nums">{sentinel.percentDone}%</span>
              </span>
            </TableCell>
            <TableCell className="tabular-nums">{sentinel.medianMinutesPerCase === null ? NONE : sentinel.medianMinutesPerCase.toFixed(1)}</TableCell>
            <TableCell className="tabular-nums">{sentinel.rushed}</TableCell>
            <TableCell className="tabular-nums">{sentinel.lastSubmittedAt ? format(parseUtcDate(sentinel.lastSubmittedAt), 'd MMM, HH:mm') : NONE}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

export function BenchmarkProgress() {
  const progress = useAtomValue(benchmarkProgressQueryAtom);
  if (progress.isError) {
    return (
      <p role="alert" className="text-[13px] text-red-600">
        {getApiErrorMessage(progress.error, 'Could not load the progress.')}
      </p>
    );
  }
  if (progress.isPending) return <Skeleton className="h-40 w-full rounded-lg" />;
  const { overall, rows } = progress.data;
  return (
    <div className="flex flex-col gap-6">
      {overall.needed > 0 && (
        <div className="flex items-center gap-3 text-[13px] text-text-body">
          <span>
            Answers submitted (last 7 frame days): <span className="tabular-nums">{overall.submitted} of {overall.needed}</span>
          </span>
          <Progress value={Math.round((100 * overall.submitted) / overall.needed)} aria-label="Answers submitted" className="w-40" />
        </div>
      )}
      <SentinelTable progress={progress.data} />
      {rows.length === 0 ? (
        <p className="py-10 text-center text-[13px] text-gray-800">No cases yet.</p>
      ) : (
        <Table aria-label="Progress per brand and frame day">
          <TableHeader>
            <TableRow>
              <TableHead>Brand</TableHead>
              <TableHead>Frame day</TableHead>
              <TableHead>Cases</TableHead>
              <TableHead>Sentinel</TableHead>
              <TableHead>Submitted</TableHead>
              <TableHead>In progress</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.flatMap((row) => {
              const reviewers = row.reviewers.length ? row.reviewers : [null];
              return reviewers.map((reviewer, index) => (
                <TableRow key={`${row.accountId}-${row.frameDay}-${reviewer?.reviewer ?? 'none'}`}>
                  <TableCell>{index === 0 ? GoldenAdapter.brandName(row.accountId) : ''}</TableCell>
                  <TableCell className="tabular-nums">{index === 0 ? row.frameDay : ''}</TableCell>
                  <TableCell className="tabular-nums">{index === 0 ? row.cases : ''}</TableCell>
                  <TableCell>{reviewer?.reviewer ?? 'No answers yet'}</TableCell>
                  <TableCell className="tabular-nums">{reviewer ? reviewer.submitted : ''}</TableCell>
                  <TableCell className="tabular-nums">{reviewer ? reviewer.inProgress : ''}</TableCell>
                </TableRow>
              ));
            })}
          </TableBody>
        </Table>
      )}
    </div>
  );
}
